import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Paperclip, Trash2 } from 'lucide-react';
import { type ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { type FileDownloadUrl } from '@apm/contracts';
import { EmptyState, ErrorState, Skeleton } from '../../../components/ui/states';
import { UploadButton } from '../../../components/ui/upload-button';
import { errorMessage } from '../../../lib/error-message';
import { apiFetch } from '../../../lib/http';
import { humanSize } from '../../../lib/bytes';
import { uploadFile } from '../../../lib/upload';
import { collaborationApi, collaborationKeys } from '../api';
import { useOrgIsPersonal } from '../../auth/use-org-is-personal';

/**
 * Danh sách tệp đính kèm.
 *
 * Tải về đi qua presigned URL của `APM-202`: bấm nút thì xin URL rồi để trình
 * duyệt tự tải thẳng từ S3. Tệp **không** bao giờ chạy qua Node (luật #3) —
 * chỉ có một instance, và stream một tệp 500MB là chiếm băng thông của cả
 * hệ thống.
 */
export function AttachmentList({
  taskId,
  projectId,
  canEdit,
  canUpload = false,
}: {
  taskId: string;
  /**
   * Bắt buộc khi `canUpload`: `uploadFile` cần `projectId` để xin presigned
   * URL (luật #3 — file đi thẳng S3, app chỉ cấp quyền). Bỏ trống ở nơi không
   * cho upload thì không ai phải truyền một id không dùng tới.
   */
  projectId?: string;
  canEdit: boolean;
  /**
   * Cho phép đính kèm tệp mới — tách khỏi `canEdit` vì hai quyền khác nhau:
   * "Việc của tôi" cho người phụ trách đính tài liệu liên quan (`canUpload`)
   * nhưng không cho sửa cấu trúc task (`canEdit=false`) — xem
   * `task-detail-popup.tsx`. Mặc định tắt: board/gantt chưa từng có nút này,
   * bật mặc định sẽ đổi hành vi ở hai nơi không ai yêu cầu.
   */
  canUpload?: boolean;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  /** Không gian cá nhân không đính kèm được tệp — APM-PSPACE-b: ẩn hẳn nút, không làm mờ. */
  const orgIsPersonal = useOrgIsPersonal();

  const attachments = useQuery({
    queryKey: collaborationKeys.attachments(taskId),
    queryFn: () => collaborationApi.attachments(taskId),
  });

  const detach = useMutation({
    mutationFn: (fileId: string) => collaborationApi.detach(taskId, fileId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: collaborationKeys.attachments(taskId) }),
        queryClient.invalidateQueries({ queryKey: collaborationKeys.activity(taskId) }),
      ]);
    },
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fileId = await uploadFile(file, { projectId: projectId! });
      await collaborationApi.attach(taskId, fileId);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: collaborationKeys.attachments(taskId) }),
        queryClient.invalidateQueries({ queryKey: collaborationKeys.activity(taskId) }),
      ]);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  async function download(fileId: string): Promise<void> {
    const { url } = await apiFetch<FileDownloadUrl>(`/files/${fileId}/url`);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  // Nút upload đứng **trên** mọi trạng thái (đang tải/lỗi/rỗng/có dữ liệu):
  // danh sách rỗng vẫn phải đính được tệp đầu tiên, không chỉ danh sách đã có.
  const uploader = canUpload && !orgIsPersonal && (
    <div className="flex items-center gap-2">
      <input
        ref={fileInput}
        type="file"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) upload.mutate(file);
          event.target.value = '';
        }}
      />
      <UploadButton
        size="sm"
        variant="outline"
        disabled={upload.isPending}
        onClick={() => fileInput.current?.click()}
      >
        {upload.isPending ? t('collaboration.uploading') : t('collaboration.addAttachment')}
      </UploadButton>
    </div>
  );

  if (attachments.isPending) {
    return (
      <div className="flex flex-col gap-2">
        {uploader}
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }
  if (attachments.isError) {
    return (
      <div className="flex flex-col gap-2">
        {uploader}
        <ErrorState
          message={errorMessage(attachments.error, t)}
          onRetry={() => void attachments.refetch()}
        />
      </div>
    );
  }
  if (attachments.data.length === 0) {
    return (
      <div className="flex flex-col gap-2">
        {uploader}
        <EmptyState
          title={t('collaboration.noAttachments')}
          description={t('collaboration.noAttachmentsHelp')}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {uploader}

      <ul className="divide-y divide-border rounded-DEFAULT border border-border">
        {attachments.data.map((file) => (
          <li key={file.id} className="flex items-center gap-3 px-3 py-2 text-sm">
            <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />

            <div className="min-w-0 flex-1">
              <div className="truncate">{file.filename}</div>
              <div className="text-xs text-muted-foreground">
                {humanSize(file.sizeBytes)} ·{' '}
                {file.uploadedByName ?? t('collaboration.unknownAuthor')}
              </div>
            </div>

            <button
              type="button"
              aria-label={t('collaboration.download')}
              className="text-muted-foreground hover:text-foreground"
              onClick={() => void download(file.id)}
            >
              <Download className="h-4 w-4" aria-hidden />
            </button>

            {canEdit && (
              <button
                type="button"
                aria-label={t('collaboration.removeAttachment')}
                className="text-muted-foreground hover:text-rag-red"
                onClick={() => detach.mutate(file.id)}
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
